import React, { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useParams, Link, useLocation, useHistory } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'
import { Formik, Form } from 'formik'
import { api } from '../../services/api'
import Card from '../../components/Card'
import { FiCheck, FiCheckCircle, FiXCircle, FiMail, FiArrowRight, FiX, FiHelpCircle, FiInfo } from 'react-icons/fi'
import Spinner from '../../components/Spinner'
import PasswordTextbox from '../../components/Form/PasswordTextbox'
import ProgressBar from '../../components/ProgressBar'
import Yup from '../../services/yup'
import { passwordChecker } from '../../services/password-checker'
import { Button } from '../../components/Form'

const validation = Yup.object({
	password: Yup.string()
		.required('Senha obrigatória.')
		.min(6, 'A senha deve conter pelo menos 6 caracteres.'),
	'password-confirm': Yup.string()
		.required('Confirme a senha digitada.')
		.oneOf([Yup.ref('password')], 'As senhas não coincidem.'),
})

export default function () {
	const { token } = useParams()

	const location = useLocation()

	const history = useHistory()

	const [isTokenValid, setIsTokenValid] = useState(null)
	const [isConsentimentoConfirmado, setIsConsentimentoConfirmado] = useState(false)
	const [forcaSenha, setForcaSenha] = useState(0)
	const [jaTemUsuario, setJaTemUsuario] = useState(false)
	const [isPending, setIsPending] = useState(true)
	const [isConsentimentoConfirmadoComNovoUsuario, setIsConsentimentoConfirmadoComNovoUsuario] = useState(false)
	const [conviteRecusado, setConviteRecusado] = useState(false)

	const { data: consentimento } = useQuery({
		queryKey: ['validar-token-consentimento'],
		queryFn: async () => {
			const response = await api.get(`membro_grupo/consentimento/${token}/validar`)

			setIsTokenValid(response.valido)

			if(response.valido) {
				const jaTemUsuario = !!response.pessoa.usuario

				setJaTemUsuario(jaTemUsuario)
			}

			setIsPending(false)

			return response
		},
		refetchOnWindowFocus: false
	})

	const { mutate: handleConsentir } = useMutation({
		mutationFn: async data => {
			try {
				await api.put('membro_grupo/consentir', { token, ...data })

				setIsConsentimentoConfirmadoComNovoUsuario(true)
			} catch (e) {
				toast.error(e.msg)
			} finally {
				setIsPending(false)
			}
		}
	})

	async function handleConsentirAutomatico() {
		try {
			await api.put('membro_grupo/consentir_automatico', { token })

			setIsConsentimentoConfirmado(true)
		} catch (e) {
			toast.error(e.msg)
		}
	}

	async function handleRecusarConvite() {
		try {
			await api.put('membro_grupo/negar_consentimento', { token })

			setConviteRecusado(true)
		} catch(e) {
			toast.error(e.msg)
		}
	}

	async function handleCheckPassword(password) {
		const { data } = await passwordChecker.post('/', { password })

		setForcaSenha(data.score)
	}

	const handleCheckPasswordDebounced = useDebouncedCallback(handleCheckPassword, 1000)

	const email = consentimento?.pessoa?.contato.find(c => c.tipo_contato_id === 6)?.contato
	const emailNaoReconhecido = !!isTokenValid && !!consentimento && !consentimento.pessoa.email

	const estaAutenticado = location.pathname.includes('/auth/')

	return (
		<div className="sm:mx-36 mt-16 mb-16 2xl:mb-[40vh] animated fadeIn faster flex justify-center">
			{isPending ? (
				<Spinner label="Aguarde..." />
			) : (
				<Card className="flex flex-col gap-8 bg-white text-slate-800 max-w-96">
					{isTokenValid ? (
						<>
							{isConsentimentoConfirmado ? (
								<>
									<div className="flex flex-col items-center gap-2">
										<FiCheckCircle size={56} className="text-emerald-400 animated zoomIn faster" />
										<h1 className="text-center text-slate-800 animated zoomIn faster delay-200ms">Convite aceito!</h1>
									</div>

									<p>Aproveite nossas soluções!</p>

									{estaAutenticado ? (
										<Button onClick={() => {
											history.goBack()
										}}>
											Ok
											<FiArrowRight size={16} />
										</Button>
									) : (
										<Button onClick={() => {
											history.push('/login')
										}}>
											Ir para o login
											<FiArrowRight size={16} />
										</Button>
									)}
								</>
							) : conviteRecusado ? (
								<>
									<div className="flex flex-col items-center gap-2">
										<FiCheckCircle size={56} className="text-emerald-400 animated zoomIn faster" />
										<h1 className="text-center text-slate-800 animated zoomIn faster delay-200ms">Convite recusado!</h1>
									</div>

									<p>Você recusou o convite de <span className="font-semibold">{consentimento.titular.nome}</span> para fazer parte do grupo de usuários.</p>

									{estaAutenticado ? (
										<Button onClick={() => {
											history.goBack()
										}}>
											Ok
											<FiArrowRight size={16} />
										</Button>
									) : (
										<Button onClick={() => {
											history.push('/login')
										}}>
											Ir para o login
											<FiArrowRight size={16} />
										</Button>
									)}
								</>
							) : jaTemUsuario ? (
								<>
									<div className="flex flex-col items-center gap-2">
										<FiHelpCircle size={56} className="text-yellow-500" />
										<h1 className="text-center text-slate-800">Aceita o convite?</h1>
									</div>

									<p><span className="font-semibold">{consentimento.titular.nome}</span> convidou você a gerenciar seu <span className="font-semibold">{consentimento.assinatura.plano.modulo.descricao}</span>.</p>

									<p>Gostaria de aceitar o convite?</p>

									{estaAutenticado ? (
										<div className="flex flex-col-reverse gap-2 md:flex-row w-full justify-end">
											<Button className="bg-red-700" onClick={() => {
												handleRecusarConvite()
											}}>
												Não
												<FiX size={16} />
											</Button>

											<Button onClick={() => {
												handleConsentirAutomatico()
											}}>
												Sim
												<FiCheck size={16} />
											</Button>
										</div>
									) : (
										<div className="flex flex-col-reverse gap-2 md:flex-row w-full justify-end">
											<Button className="bg-red-700" onClick={() => {
												handleRecusarConvite()
											}}>
												Não
												<FiX size={16} />
											</Button>

											<Button onClick={() => {
												handleConsentirAutomatico()
											}}>
												Sim
												<FiCheck size={16} />
											</Button>
										</div>
									)}
								</>
							) : isConsentimentoConfirmadoComNovoUsuario ? (
								<>
									<div className="flex flex-col items-center gap-2">
										<FiCheckCircle size={56} className="text-emerald-400" />
										<h1 className="text-center text-slate-800">Seu e-mail está confirmado!</h1>
									</div>

									<p>Agora você faz parte do grupo de <span className="font-semibold">{consentimento.titular.nome}</span>.</p>

									<p>Use seu e-mail e a senha definida para acessar sua conta e começar a usar nossas soluções:</p>

									<p className="py-1 px-2 bg-emerald-200 rounded-md w-fit self-center flex gap-2 items-center">
										<FiMail size={16} />
										{email}
									</p>

									<Link to="/login" className="button">
										Ir para o login
										<FiArrowRight size={16} />
									</Link>
								</>
							) : (
								<div className="flex flex-col gap-4">
									<h1 className="text-slate-800 mx-2">{`Olá, ${consentimento.pessoa.nome}`}</h1>

									<p className="mx-2">Defina uma senha segura para acessar nossas soluções.</p>

									<Formik
										onSubmit={handleConsentir}
										initialValues={{
											password: '',
											'password-confirm': ''
										}}
										validationSchema={validation}
									>
										{({ isSubmitting, values }) => (
											<Form className="flex flex-col gap-2">
												<PasswordTextbox
													name="password"
													label="Senha"
													onChange={pwd => {
														if(pwd) {
															handleCheckPasswordDebounced(pwd)
														} else {
															setForcaSenha(0)
														}
													}}
												/>

												<ProgressBar
													visible={!!values.password?.length && forcaSenha > 0}
													value={forcaSenha}
													rotulada
													rotulos={[
														{ label: 'Sem senha', color: 'bg-gray-400' },
														{ label: 'Muito fraca', color: 'bg-red-500' },
														{ label: 'Fraca', color: 'bg-orange-500' },
														{ label: 'Média', color: 'bg-yellow-500' },
														{ label: 'Forte', color: 'bg-lime-500' },
														{ label: 'Muito forte', color: 'bg-emerald-500' }
													]}
												/>

												<PasswordTextbox
													name="password-confirm"
													label="Repetir senha"
												/>

												<Button type="submit" loading={isSubmitting}>
													Confirmar senha
													<FiCheck size={16} />
												</Button>
											</Form>
										)}
									</Formik>
								</div>
							)}
						</>
					) : emailNaoReconhecido ? (
						<>
							<div className="flex flex-col items-center gap-2">
								<FiXCircle size={56} className="text-red-500" />
								<h1 className="text-center text-slate-800">E-mail não reconhecido!</h1>
							</div>

							<p className="text-center">Verifique o endereço acessado e tente novamente.</p>
						</>
					) : (
						<>
							<div className="flex flex-col items-center gap-2">
								<FiInfo size={56} className="text-sky-500" />
								<h1 className="text-center text-slate-800">Seja bem-vindo!</h1>
							</div>

							<p className="text-center">Este convite já foi aceito anteriormente.</p>
						</>
					)}
				</Card>
			)}
		</div>
	)
}
